/**
 * Pagination — Tiptap extension that enforces real page boundaries
 * inside the contenteditable.
 *
 * Why this exists:
 *   The editor runs in a single contenteditable surface, so content
 *   naturally flows top-to-bottom without respecting page edges. To
 *   make the on-screen experience match printed output (Word-style
 *   pages with top/bottom margins respected on EVERY page), we walk
 *   the doc on every change, measure each top-level block's
 *   rendered height, and insert non-editable widget "spacer" divs
 *   at the boundaries where a block would otherwise cross into the
 *   bottom-margin or next-page-top-margin areas.
 *
 *   Each spacer pushes the following block down to the start of the
 *   next page's content area:
 *
 *     spacerHeight = nextPageTopY - currentBlockBottomY
 *
 *   PDF export already paginates via Chromium's `break-inside:
 *   avoid` print CSS, so this extension is editor-only.
 *
 * Splitting depth:
 *   v1 split between top-level blocks only — that left huge gaps
 *   when an entire topic got pushed to the next page because a
 *   later bullet didn't fit. v2 walks INTO splittable parents
 *   (topic, attendeesBlock, actionItemsBlock) and applies the
 *   pagination rule to each child individually. So a long topic
 *   now keeps its title + first several bullets on page 1 and
 *   flows the rest to page 2, just like Word.
 *
 * Stability + correctness:
 *   v4 computes each block's true POST-PAGINATION position by
 *   first stripping the effect of EXISTING spacers (collected
 *   via querySelectorAll('.tt-page-spacer') and subtracted in
 *   cumulative fashion) and then adding back the height of NEW
 *   spacers already decided in this pass. The page-overflow
 *   check and the spacer-height calculation both use this
 *   `finalTop`, which means:
 *     1. Decisions are stable across runs (same input → same
 *        decoration set, no ResizeObserver feedback loop).
 *     2. A bullet right after a pushed-to-next-page topic title
 *        is correctly placed on the new page; the earlier v3
 *        sized its spacer off the bullet's natural-top, ignoring
 *        the title's push, so the bullet landed below where it
 *        should and visibly crossed the next page boundary.
 *   Block margins are accounted for automatically because
 *   getBoundingClientRect gives the post-margin-collapse position.
 *
 * Limitations:
 *   - Single child taller than one page still overflows (e.g., a
 *     bullet whose body wraps to 30 lines). Acceptable for
 *     typical meeting content.
 *   - First compute on a fresh doc happens after React renders,
 *     so there's a 1-frame window where pagination isn't yet
 *     applied.
 */
import { Extension } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const PAGE_HEIGHT_PX = 11 * 96; // 1056
// Visible gap between pages (must match PaginatedPaper's
// PAGE_GAP_PX). Spacers push content past this gap so the next
// block lands in the next page's content area, not in the
// gray gap zone.
const PAGE_GAP_PX_DEFAULT = 28;
// PAGE_SLOT_PX_DEFAULT (= PAGE_HEIGHT_PX + PAGE_GAP_PX_DEFAULT) was
// declared here for symmetry with the dynamic getPageSlotPx() helper
// but had no readers. Removed during audit-fix sweep.
// PAGE 1 top margin = 1in (room for the large letterhead logo).
const FIRST_TOP_MARGIN_PX_DEFAULT = 1 * 96; // 96
// PAGES 2+ top margin = 0.5in (smaller, matches continuation pages
// of the .docx + .pdf exports which only need to clear the small
// running-header logo on the LEFT margin).
const CONTINUATION_TOP_MARGIN_PX_DEFAULT = 0.5 * 96; // 48
// Bottom margin: 0.5" — matches the .docx PAGE.margin.bottom and
// the .pdf @page margin-bottom exactly. The page-number widget is
// positioned at 0.3" from page edge (BELOW the content area) so
// content + widget don't collide.
const BOTTOM_MARGIN_PX_DEFAULT = 0.5 * 96; // 48 — uniform on all pages
// Reserve extra clearance ABOVE the bottom margin band so the last
// line of body content never visually crowds the "Page X of Y"
// footer widget. Without this buffer, a block whose bottom edge
// lands exactly at the content boundary (1996 in view.dom Y on
// page 2) leaves only ~19px of vertical gap to the page-number
// widget at 0.3" from page bottom — which font ascenders/
// descenders eat, so the body's last line and "Page X of Y" appear
// on the same visual line. The strict `naturalBottom > pageEnd`
// check also fails when a block's bottom equals the boundary
// (subpixel rounding makes this common in real docs), so the block
// stays on the page and the cramming is unavoidable without a
// buffer.
//
// Trade-off: the editor's enforced break point sits ~0.375" above
// the .docx/.pdf renderer's actual margin boundary, so the editor
// preview can show one more page than the actual export for docs
// whose last content sits right at the boundary. Accepted because
// (a) DOCX/PDF use their own renderers — page count parity with
// the preview was never strict, and (b) the visual cramming the
// pilot reported is far worse than a one-page editor offset.
const BREATHING_ROOM_PX = 36;
// Page 0 usable content height (smaller because of the larger
// letterhead top margin).
const FIRST_PAGE_CONTENT_HEIGHT_DEFAULT =
  PAGE_HEIGHT_PX -
  FIRST_TOP_MARGIN_PX_DEFAULT -
  BOTTOM_MARGIN_PX_DEFAULT -
  BREATHING_ROOM_PX; // 876
// Pages 2+ usable content height (larger — only loses the smaller
// continuation top + bottom margins).
const CONTINUATION_PAGE_CONTENT_HEIGHT_DEFAULT =
  PAGE_HEIGHT_PX -
  CONTINUATION_TOP_MARGIN_PX_DEFAULT -
  BOTTOM_MARGIN_PX_DEFAULT -
  BREATHING_ROOM_PX; // 924

/** Top-level node types whose children should be paginated
 *  individually (one level deep). Letting the algorithm split
 *  inside these containers means a long topic keeps its title +
 *  first bullets on the current page and only flows the
 *  later-overflow bullets to the next page — same as Word. */
const SPLITTABLE_PARENT_TYPES = new Set([
  'topic',
  'attendeesBlock',
  'actionItemsBlock',
]);

interface PaginationOptions {
  /** Page-1 (letterhead) usable content height in px. Default 912
   *  (= 11" - 1" top - 0.5" bottom @ 96dpi). */
  firstPageContentHeightPx: number;
  /** Pages-2+ (continuation) usable content height in px.
   *  Default 960 (= 11" - 0.5" top - 0.5" bottom). */
  continuationPageContentHeightPx: number;
  /** Page-1 top margin in px. Default 96 (1in for letterhead). */
  firstTopMarginPx: number;
  /** Pages-2+ top margin in px. Default 48 (0.5in). */
  continuationTopMarginPx: number;
  /** Bottom margin in px. Uniform across pages. Default 48. */
  bottomMarginPx: number;
  /** Vertical gap between pages in px. Must match PaginatedPaper's
   *  PAGE_GAP_PX. Default 28. */
  pageGapPx: number;
}

const paginationKey = new PluginKey<DecorationSet>('pagination');

/** Compare two DecorationSets — used to suppress no-op dispatches.
 *  Cheap shallow comparison: same count + same {from, to} for each. */
function decorationsEqual(a: DecorationSet, b: DecorationSet): boolean {
  const aDecos = a.find();
  const bDecos = b.find();
  if (aDecos.length !== bDecos.length) return false;
  for (let i = 0; i < aDecos.length; i++) {
    if (aDecos[i].from !== bDecos[i].from) return false;
    if (aDecos[i].to !== bDecos[i].to) return false;
  }
  return true;
}

export const Pagination = Extension.create<PaginationOptions>({
  name: 'pagination',

  addOptions() {
    return {
      firstPageContentHeightPx: FIRST_PAGE_CONTENT_HEIGHT_DEFAULT,
      continuationPageContentHeightPx: CONTINUATION_PAGE_CONTENT_HEIGHT_DEFAULT,
      firstTopMarginPx: FIRST_TOP_MARGIN_PX_DEFAULT,
      continuationTopMarginPx: CONTINUATION_TOP_MARGIN_PX_DEFAULT,
      bottomMarginPx: BOTTOM_MARGIN_PX_DEFAULT,
      pageGapPx: PAGE_GAP_PX_DEFAULT,
    };
  },

  addProseMirrorPlugins() {
    const firstContent = this.options.firstPageContentHeightPx;
    const contContent = this.options.continuationPageContentHeightPx;
    const firstTop = this.options.firstTopMarginPx;
    const contTop = this.options.continuationTopMarginPx;
    // PAGE_SLOT_PX is the period of the pagination cycle: page
    // height + gap. Page N's TOP starts at N * pageSlot in
    // view.dom coordinates (since the editor's content begins at
    // the start of page 0's content area, which sits at view.dom
    // Y=0; the gap below each page is a no-content zone the
    // spacers push past).
    const pageSlot = PAGE_HEIGHT_PX + this.options.pageGapPx;
    // Asymmetric margin offset: page 1 had a 1in top margin, page 2+
    // only have 0.5in. So in view.dom coordinates, page N's content
    // top is N*pageSlot - (firstTop - contTop) for N >= 1.
    // (Page 0's content top is 0 by definition.)
    const continuationOffset = firstTop - contTop; // 48
    const pageContentTop = (pageIdx: number): number =>
      pageIdx === 0 ? 0 : pageIdx * pageSlot - continuationOffset;
    const pageContentBottom = (pageIdx: number): number =>
      pageIdx === 0 ? firstContent : pageContentTop(pageIdx) + contContent;
    /** Which page index does the given view.dom Y land on? */
    const pageIdxFor = (viewDomY: number): number => {
      if (viewDomY <= firstContent) return 0;
      // After page 0 we're in the asymmetric realm: (Y + offset) / slot.
      return Math.floor((viewDomY + continuationOffset) / pageSlot);
    };

    return [
      new Plugin<DecorationSet>({
        key: paginationKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, oldSet) {
            const meta = tr.getMeta(paginationKey);
            if (meta instanceof DecorationSet) return meta;
            return oldSet.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return paginationKey.getState(state);
          },
        },
        view(view) {
          let raf = 0;
          let lastDoc = view.state.doc;

          const recompute = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => doRecompute(view));
          };

          const doRecompute = (view: EditorView) => {
            const decorations: Decoration[] = [];
            const editorRect = view.dom.getBoundingClientRect();

            // Detect cumulative CSS `zoom` applied to any ancestor
            // (ScaledPaper sets `style={{ zoom: effectiveZoom }}`
            // for the auto-fit + manual zoom toolbar). CSS zoom
            // scales everything inside — including
            // getBoundingClientRect() values — but our page-
            // boundary constants (PAGE_HEIGHT_PX, pageContentTop,
            // etc.) are LOGICAL CSS pixels at zoom=1. Without
            // dividing rect measurements by this factor, the
            // algorithm thinks blocks are smaller at <1 zoom and
            // larger at >1, so page breaks drift as the user
            // zooms. Static page breaks require all measurements
            // converted back to logical coordinates.
            let zoomFactor = 1;
            for (
              let cur: HTMLElement | null = view.dom;
              cur;
              cur = cur.parentElement
            ) {
              const inline = cur.style && cur.style.zoom;
              if (inline) {
                const v = Number.parseFloat(inline);
                if (!Number.isNaN(v) && v > 0) zoomFactor *= v;
                continue;
              }
              const computed = window.getComputedStyle(cur).zoom;
              if (computed && computed !== 'normal' && computed !== '1') {
                const v = Number.parseFloat(computed);
                if (!Number.isNaN(v) && v > 0 && v !== 1) zoomFactor *= v;
              }
            }
            const inv = 1 / zoomFactor;

            // Snapshot every spacer we've previously placed in
            // view.dom, with its current top + height (converted
            // to LOGICAL pixels via inv-zoom). We need CUMULATIVE
            // subtraction (not just immediate-previous-sibling)
            // because spacers placed inside a parent (e.g. before
            // a topic's title) shift every later DOM node —
            // including the topic's own bullets, whose
            // previousElementSibling is the title, not the spacer.
            // Without summing all spacers above a block, those
            // bullets get measured at their shifted positions,
            // pagination decides they overflow when they don't,
            // and the next ResizeObserver tick oscillates the
            // decision — producing the 9↔10-page flicker on
            // meetings whose topics cross a page boundary.
            const existingSpacers: Array<{ top: number; height: number }> = [];
            view.dom.querySelectorAll('.tt-page-spacer').forEach((el) => {
              if (!(el instanceof HTMLElement)) return;
              const r = el.getBoundingClientRect();
              existingSpacers.push({
                top: (r.top - editorRect.top) * inv,
                height: r.height * inv,
              });
            });
            existingSpacers.sort((a, b) => a.top - b.top);

            const cumulativeSpacerHeightAbove = (yInView: number): number => {
              let sum = 0;
              for (const s of existingSpacers) {
                if (s.top < yInView) sum += s.height;
                else break;
              }
              return sum;
            };

            // Track cumulative height of spacers we ADD in THIS
            // pass. Decisions for later blocks need to account for
            // earlier-in-this-pass pushes too — without this, a
            // bullet right after a pushed-to-next-page topic title
            // gets sized as if no push happened, so its spacer
            // lands the bullet at the wrong y and the bullet
            // visibly crosses the next page boundary.
            let newSpacerSumAbove = 0;

            // Place a single block. Uses DOM measurements directly
            // (rect.top relative to view.dom) so block margins are
            // accounted for naturally — getBoundingClientRect
            // gives us the post-margin-collapse position.
            //
            // Two adjustments turn that DOM-current-top into the
            // block's true POST-PAGINATION position for THIS pass:
            //   1. Subtract the sum of EXISTING spacers above the
            //      block (they're in the DOM, but we replace the
            //      DecorationSet entirely so they'll be removed on
            //      dispatch — pretend they're gone).
            //   2. Add the sum of NEW spacers we've already
            //      decided to insert above this block in this
            //      pass (they aren't in the DOM yet, but they
            //      will be after dispatch).
            // The page-overflow check + spacer sizing both use
            // this `finalTop` so spacers correctly target the
            // next page's content top in the post-pagination
            // layout.
            const placeAtomic = (offset: number, dom: HTMLElement) => {
              const rect = dom.getBoundingClientRect();
              // Convert visual rect dimensions to LOGICAL pixels
              // so they match our zoom=1 page-geometry constants.
              const blockHeight = rect.height * inv;
              if (blockHeight === 0) return;

              const currentTop = (rect.top - editorRect.top) * inv;
              const naturalTop =
                currentTop - cumulativeSpacerHeightAbove(currentTop);
              const finalTop = naturalTop + newSpacerSumAbove;
              const finalBottom = finalTop + blockHeight;
              // Which page does this block sit on AFTER applying
              // all pushes so far in this pass?
              const pageIdx = pageIdxFor(finalTop);
              const pageStart = pageContentTop(pageIdx);
              const pageEnd = pageContentBottom(pageIdx);

              // Push if the post-pagination position crosses the
              // bottom-margin line AND the block isn't already at
              // the very top of a page (a block already at page
              // top can't be helped by another push — the
              // overflow is the known "single child taller than
              // one page" limitation).
              if (finalBottom > pageEnd && finalTop > pageStart) {
                // Target = top of NEXT page's content area
                // (accounting for the asymmetric continuation top).
                const targetY = pageContentTop(pageIdx + 1);
                const spacerHeight = targetY - finalTop;

                decorations.push(
                  Decoration.widget(
                    offset,
                    () => createSpacer(spacerHeight),
                    {
                      // side: -1 places the widget just BEFORE the
                      // node at this position. 'key' lets ProseMirror
                      // reuse the DOM across renders so we don't
                      // tear-down the spacer on every keystroke.
                      side: -1,
                      key: `page-spacer-${offset}-${spacerHeight}`,
                    },
                  ),
                );
                newSpacerSumAbove += spacerHeight;
              }
            };

            // Walk a node, choosing between atomic placement and
            // recursing into its children. Containers like topic /
            // attendeesBlock / actionItemsBlock recurse so their
            // children can break independently across pages.
            const walk = (
              node: ProseMirrorNode,
              offset: number,
              depth: number,
            ) => {
              if (depth === 0 && SPLITTABLE_PARENT_TYPES.has(node.type.name)) {
                let childPos = offset + 1;
                node.forEach((childNode) => {
                  walk(childNode, childPos, depth + 1);
                  childPos += childNode.nodeSize;
                });
                return;
              }

              const dom = view.nodeDOM(offset);
              if (!(dom instanceof HTMLElement)) return;
              placeAtomic(offset, dom);
            };

            view.state.doc.forEach((node, offset) => {
              walk(node, offset, 0);
            });

            const newSet = DecorationSet.create(view.state.doc, decorations);
            const oldSet = paginationKey.getState(view.state);
            if (!oldSet || !decorationsEqual(oldSet, newSet)) {
              const tr = view.state.tr.setMeta(paginationKey, newSet);
              view.dispatch(tr);
            }
          };

          // Initial compute after first render.
          recompute();
          // Re-run after web fonts load — line-wrap heights change
          // when the real font replaces the fallback.
          if (typeof document !== 'undefined' && document.fonts) {
            document.fonts.ready.then(() => recompute()).catch(() => {});
          }

          // Recompute on ANY size change of view.dom. Height
          // matters too: ReactNodeViewRenderers (TopicView,
          // BulletNodeView, etc.) render their React content
          // asynchronously after the editor view is created, so
          // the initial rAF-scheduled recompute measures bullets
          // BEFORE their final heights are known. Without a
          // height-based observer, those mismeasurements stuck —
          // the user saw bullets crossing every page boundary on
          // page reload, but Finalize "fixed" it (because the
          // editor remounts when m.status flips draft→finalized
          // and the second mount measures correct heights).
          //
          // The pagination algorithm is loop-safe because:
          //   - cumulativeSpacerHeightAbove subtracts EXISTING
          //     spacers, recovering each block's natural top.
          //   - newSpacerSumAbove tracks NEW pushes in this pass,
          //     making decisions converge to a fixed point.
          //   - decorationsEqual short-circuits the dispatch
          //     when this pass's set matches the previous one,
          //     so the second-pass verify after a dispatch
          //     becomes a no-op.
          // PaginatedPaper's HYSTERESIS_PX further dampens
          // pageCount churn from sub-pixel reflows.
          const ro = new ResizeObserver(recompute);
          ro.observe(view.dom);

          return {
            update(view) {
              if (view.state.doc !== lastDoc) {
                lastDoc = view.state.doc;
                recompute();
              }
            },
            destroy() {
              ro.disconnect();
              cancelAnimationFrame(raf);
            },
          };
        },
      }),
    ];
  },
});

/** Build the non-editable spacer DOM element. Includes a subtle
 *  visual cue (faint horizontal line) so the user can tell where
 *  the page break landed. The spacer is fully opaque to clicks via
 *  pointer-events:none; ProseMirror skips it during cursor
 *  navigation because it's a widget decoration, not a real node. */
function createSpacer(heightPx: number): HTMLElement {
  const el = document.createElement('div');
  el.className = 'tt-page-spacer';
  el.contentEditable = 'false';
  el.setAttribute('aria-hidden', 'true');
  el.style.height = `${heightPx}px`;
  el.style.userSelect = 'none';
  el.style.pointerEvents = 'none';
  return el;
}
